import React, { useEffect, useState } from "react";
import "./EditDeviceNameModal.css";
import { Modal } from "react-bootstrap";
import { titles } from "constants/titles";
import CustomButton from "components/widgets/custom-button/CustomButton";
import MessageSnackBar from "components/widgets/message-snackbar/MessageSnackBar";
import { useDispatch, useSelector } from "react-redux";
import { updateDeviceName } from "redux/Devices/actions";
import { isEmpty, isSessionTimeExpired } from "utility";
import { useHistory } from "react-router-dom";
import alerts from "constants/alerts";
import Loader from "react-spinners/ClipLoader";
import SessionExpiredPopup from "../session-expired-popup/SessionExpiredPopup";
import { CrossIconBtn } from "assets/SVGs/SVGs";
import { useAdvancedValidation } from "hooks/useAdvancedValidation";

const EditDeviceNameModal = ({ show, setShow, onCancel }) => {
  const [deviceName, setDeviceName] = useState("");
  const [verificationPopUp, setVerificationPopUp] = useState(false);
  const [fieldError, setFieldError] = useState(false);
  const [validationTriggered, setValidationTriggered] = useState(false);

  // Use advanced validation hook (same as mobile)
  const { snackbarState, dismissSnackbar, onApiResponse } = useAdvancedValidation();
  const { selectedDeviceDetails, loading } = useSelector(
    (store) => store?.devices
  );
  const userData = useSelector((store) => store?.auth?.userData);
  const { selectedMedicalUser } = useSelector(
    (store) => store?.medicalAlertUsers
  );

  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    if (show) {
      setDeviceName(selectedDeviceDetails?.device_name);
      dismissSnackbar();
      setValidationTriggered(false);
      setFieldError(false);
    }
  }, [show]);

  const onClose = () => {
    setShow(false);
    dismissSnackbar();
    setValidationTriggered(false);
    setFieldError(false);
  };

  const handleClose = () => {
    onClose();
  };

  const handleDeviceNameChange = (value) => {
    setDeviceName(value);
    // Clear error when user types
    if (validationTriggered && fieldError) {
      setFieldError(false);
      dismissSnackbar();
    }
  };

  const onUpdateDeviceName = () => {
    let payload = {
      medical_alert_user_id: selectedDeviceDetails?.medical_alert_user_id,
      user_device_id: selectedDeviceDetails?.user_device_id,
      device_name: deviceName,
    };
    updateDeviceName({
      dispatch,
      payload,
      history,
      setSnackBarMessage: onApiResponse,
      onClose,
      medical_alert_user_id: selectedMedicalUser?.medical_alert_user_id,
      userType: userData?.isGuestUser ? "guestuser" : "accountowner",
    });
  };

  const onSaveChanges = async () => {
    setValidationTriggered(true);
    let timeDifference = await isSessionTimeExpired();
    
    if (isEmpty(deviceName)) {
      setFieldError(true);
      // Show validation error (manual dismiss, not auto-hide)
      onApiResponse(alerts?.enter_device_name, "error", 0);
      return;
    } else if (timeDifference && !userData?.isGuestUser) {
      setVerificationPopUp(true);
      return;
    } else {
      onUpdateDeviceName();
    }
  };

  return (
    <Modal
      backdrop="true"
      aria-labelledby="contained-modal-title-vcenter"
      className="edm_container"
      show={show}
      onHide={handleClose}
    >
      {/* Snackbar inside modal, above white card (same as mobile) */}
      {snackbarState?.show && (
        <div className="modal_snackbar_wrapper">
          <MessageSnackBar
            message={snackbarState?.message}
            isVisible={true}
            type={snackbarState?.type}
            autoHideDuration={snackbarState?.autoHideDuration}
            onDismiss={dismissSnackbar}
          />
        </div>
      )}

      {loading && (
        <div className="edm_loader_container">
          <Loader color="#DB4E3B" />
        </div>
      )}
      <Modal.Body className="edm_body">
          <div className="edm_header">
            <CrossIconBtn
              className="edm_cross_icon"
              onClick={() => setShow(!show)}
            />
          </div>
          <div className="edm_content">
          <h3 className="edm_title">{titles?.set_device_name}</h3>
          <p className="edm_description">
            {titles?.set_device_name_instruction}
          </p>

          <div className="edm_input_section">
            <label className="edm_input_label">{titles?.device_name}</label>
            <div className="edm_input_container">
              <input
                placeholder={titles?.device_name_placeholder}
                className="edm_input_field"
                type="text"
                value={deviceName}
                maxLength={16}
                onChange={(e) => handleDeviceNameChange(e.target.value)}
              />
              <span className="edm_char_count">
                {16 - deviceName?.length} characters left
              </span>
            </div>
          </div>

          <div className="edm_buttons">
            <CustomButton
              className="edm_save_button"
              text={titles?.save_changes}
              width="100%"
              onClick={onSaveChanges}
            />
            <CustomButton
              className="edm_cancel_button"
              text={titles?.cancel}
              width="100%"
              onClick={() => {
                onCancel();
                setShow(false);
              }}
            />
          </div>
        </div>
      </Modal.Body>
      {verificationPopUp ? (
        <SessionExpiredPopup
          isVisible={verificationPopUp}
          onClosePopup={(parentPopupHide) => {
            setVerificationPopUp(false);
            if (parentPopupHide) {
              onClose();
            }
          }}
          history={history}
        />
      ) : null}
    </Modal>
  );
};

export default EditDeviceNameModal;
